import { useState } from 'react';
import { sendMagicLink, loginWithPasskey, loginWithPassword, supportsWebAuthn } from '../auth';
import { useToast } from '../lib/toast';

// Use a hard reload after successful sign-in so App.tsx re-runs fetchMe() and
// populates the user state. nav('/dashboard') alone gets bounced by the
// RequireAuth guard because user state isn't propagated from here.
function goToDashboard() { window.location.href = '/dashboard'; }

type Mode = 'passkey' | 'password' | 'magic';

export default function LoginPage() {
  const { push } = useToast();
  const [mode, setMode] = useState<Mode>('password'); // password first — never fails to load UI
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  async function onPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await loginWithPassword(email, password);
      push('Signed in', 'success');
      goToDashboard();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function onPasskey(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await loginWithPasskey(email || undefined);
      push('Signed in', 'success');
      goToDashboard();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function onMagic(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setBusy(true); setErr(null);
    try {
      await sendMagicLink(email);
      setMagicSent(true);
      push('Check your email for a sign-in link', 'success');
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-900/40 via-ink-bg to-ink-bg p-4">
      <div className="w-full max-w-sm bg-ink-card border border-ink-border p-8 rounded-xl shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-7 h-7 rounded bg-brand-600 flex items-center justify-center text-white font-bold">O</div>
          <h1 className="text-xl font-bold">OnlyTrade</h1>
        </div>
        <p className="text-sm text-ink-muted mb-6">Product Admin</p>

        {/* Mode tabs */}
        <div className="flex gap-1 mb-5 p-0.5 bg-ink-bg border border-ink-border rounded">
          {(['password', 'passkey', 'magic'] as Mode[]).map(m => (
            <button key={m} type="button"
                    onClick={() => { setMode(m); setErr(null); setMagicSent(false); }}
                    className={`flex-1 text-xs py-1.5 rounded transition-colors ${
                      mode === m
                        ? 'bg-brand-600 text-white font-semibold'
                        : 'text-ink-muted hover:text-ink-text'
                    }`}>
              {m === 'password' ? 'Password' : m === 'passkey' ? 'Passkey' : 'Email link'}
            </button>
          ))}
        </div>

        {err && <div className="text-sm text-red-400 bg-red-950/50 border border-red-900 rounded p-2 mb-3 break-words">{err}</div>}

        {mode === 'password' && (
          <form onSubmit={onPassword}>
            <label className="block text-xs text-ink-muted mb-1 font-medium">Email</label>
            <input autoFocus required type="email" value={email} onChange={e => setEmail(e.target.value)}
                   className="w-full px-3 py-2 mb-3 bg-ink-bg border border-ink-border text-ink-text rounded text-sm focus:outline-none focus:border-brand-600" />
            <label className="block text-xs text-ink-muted mb-1 font-medium">Password</label>
            <input required type="password" value={password} onChange={e => setPassword(e.target.value)}
                   className="w-full px-3 py-2 mb-4 bg-ink-bg border border-ink-border text-ink-text rounded text-sm focus:outline-none focus:border-brand-600" />
            <button type="submit" disabled={busy}
                    className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded py-2.5 text-sm font-semibold disabled:opacity-60 transition-colors">
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <p className="text-[11px] text-ink-muted/80 mt-3 text-center">
              Forgot it? Use the <button type="button" onClick={() => setMode('magic')} className="text-brand-400 hover:underline">email link</button> tab.
            </p>
          </form>
        )}

        {mode === 'passkey' && (
          <form onSubmit={onPasskey}>
            <label className="block text-xs text-ink-muted mb-1 font-medium">Email (optional — passkey can find you without it)</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                   placeholder="you@example.com"
                   className="w-full px-3 py-2 mb-4 bg-ink-bg border border-ink-border text-ink-text rounded text-sm focus:outline-none focus:border-brand-600" />
            <button type="submit" disabled={busy || !supportsWebAuthn()}
                    className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded py-2.5 text-sm font-semibold disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
              {busy ? 'Waiting for Face ID / Touch ID…' : (<><span>🔑</span> Sign in with passkey</>)}
            </button>
            {!supportsWebAuthn() && (
              <p className="text-[11px] text-red-400 mt-3 text-center">
                This browser doesn't support passkeys. Use Password or Email link instead.
              </p>
            )}
            <p className="text-[11px] text-ink-muted/80 mt-3 text-center leading-relaxed">
              Only works on devices where you've registered a passkey. If the prompt doesn't appear, switch tabs.
            </p>
          </form>
        )}

        {mode === 'magic' && (
          magicSent ? (
            <div className="text-sm">
              <div className="bg-green-950/30 border border-green-900 text-green-300 rounded p-3 mb-3">
                Sign-in link sent to <strong>{email}</strong>. Check your inbox — link expires in 15 minutes.
              </div>
              <button onClick={() => { setMagicSent(false); }}
                      className="text-xs text-ink-muted hover:text-brand-400">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={onMagic}>
              <label className="block text-xs text-ink-muted mb-1 font-medium">Email</label>
              <input autoFocus required type="email" value={email} onChange={e => setEmail(e.target.value)}
                     className="w-full px-3 py-2 mb-4 bg-ink-bg border border-ink-border text-ink-text rounded text-sm focus:outline-none focus:border-brand-600" />
              <button type="submit" disabled={busy}
                      className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded py-2.5 text-sm font-semibold disabled:opacity-60 transition-colors">
                {busy ? 'Sending…' : 'Email me a sign-in link'}
              </button>
              <p className="text-[11px] text-ink-muted/80 mt-3 text-center">
                You'll get a link to sign in and optionally register a passkey on this device.
              </p>
            </form>
          )
        )}

        <p className="text-[11px] text-ink-muted/70 mt-6 text-center leading-relaxed">
          Admin sessions last 12 hours. Then re-auth with any method above.
        </p>
      </div>
    </div>
  );
}
